/**
 * admin/capabilities — framework-agnostic RBAC role → capability map.
 *
 * Mirrors the backend capability gating (campaign #18, Track B). The back-office
 * UI uses this to (a) decide whether a user may enter the admin area at all and
 * (b) hide/disable individual actions a role can't perform. The backend remains
 * the source of truth and re-checks every mutation; this is purely a UX layer so
 * the client never offers an action that would 403.
 *
 * Roles (broad → narrow):
 *   - super_admin — everything, including destructive GDPR removals + role grants
 *   - admin       — manage users/flags, but not destructive removals or granting
 *                   privileged roles
 *   - support     — read users + audit, ban/unban + revoke sessions (no role/flag
 *                   changes, no removals)
 *   - read_only   — read-only access to every admin panel
 *   - user        — no admin access
 */

export const ADMIN_ROLES = [
  "super_admin",
  "admin",
  "support",
  // F2-C: front-desk/cashier (records payments/leads, sees metrics; does NOT manage users).
  "staff",
  "read_only",
  "user",
] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

/** Discrete capabilities the back-office UI gates on. */
export type AdminCapability =
  | "admin.access"
  | "users.read"
  | "users.setRole"
  | "users.ban"
  | "users.revokeSessions"
  // Reset a password from the system (demo-seed model: the admin is the only
  // provisioned credential; the rest is set here). Backend: ManageUsers.
  | "users.setPassword"
  | "users.remove"
  // ── F2-C: advanced user management ──
  | "users.impersonate"
  | "users.invite"
  | "users.bulk"
  | "users.guardians"
  | "audit.read"
  | "integrations.read"
  | "integrations.testEmail"
  | "flags.read"
  | "flags.write"
  | "metrics.read"
  // Mutate platform content: themes, fonts, templates and email campaigns.
  // Mirrors `Capability::ManageContent` in `auth/rbac.rs`, which since the
  // hardening pass DOES gate the /api/admin/{font,theme,email} routers. It was
  // missing from this map, so the UI couldn't even express the restriction.
  | "content.write"
  // ── F1-B (academy platform) — mirrors `auth/rbac.rs` ──
  // Record payments (cashier). Rust: ManagePayments (staff + admin/super_admin).
  | "payments.manage"
  // CRM: leads/pipeline. Rust: ManageLeads (staff + support + admin/super_admin).
  | "leads.manage"
  // Import/export via the Excel kit. Rust: ManageData (admin/super_admin).
  | "data.manage"
  // ── MONETIZATION_QUOTAS: PLATFORM plan (academy tiers/quotas).
  // Mirrors `Capability::ManageBilling` in `auth/rbac.rs`, which gates
  // /api/admin/monetization. This is NOT `payments.manage` (that's the
  // academy's CASHIER charging its students): these are the two billing
  // levels, and whoever records a pago_movil payment doesn't decide anyone's SaaS plan.
  | "billing.read"
  | "billing.write"
  // ── CONFIG_OVER_CODE: OPERATIONAL platform configuration ──
  // Mirrors `Capability::ViewSettings` / `ManageSettings` in `auth/rbac.rs`,
  // which gate `/api/admin/config`. Reading is read_only+ (whoever diagnoses a
  // 429 needs to see the current rate limit); writing is admin+ because an
  // override moves the ceiling for ALL academies at once.
  | "settings.read"
  | "settings.write";

const READ_CAPS: AdminCapability[] = [
  "admin.access",
  "users.read",
  "audit.read",
  "integrations.read",
  "flags.read",
  "metrics.read",
  "settings.read",
];

const SUPPORT_CAPS: AdminCapability[] = [
  ...READ_CAPS,
  "users.ban",
  "users.revokeSessions",
  // Same level as ban/revoke: granted by ManageUsers on the backend.
  "users.setPassword",
  // F2-C: ManageUsers allows bulk suspend/reactivate and managing
  // invitations/guardians (same level as ban).
  "users.bulk",
  "users.invite",
  "users.guardians",
  "integrations.testEmail",
  // F1-B: Support adds ManageLeads (support = commercial follow-up).
  "leads.manage",
  // Rust parity: `Support` has ManageContent.
  "content.write",
];

// F2-C: changing role (incl. bulk) and impersonating require admin privilege.
const ADMIN_CAPS: AdminCapability[] = [
  ...SUPPORT_CAPS,
  "users.setRole",
  "users.impersonate",
  "flags.write",
  // F1-B: Admin/SuperAdmin get ALL capabilities (Rust parity).
  "payments.manage",
  "data.manage",
  // MONETIZATION_QUOTAS: parity with `ManageBilling` in Rust (admin+ and NOT
  // support/staff — `role_has_capability` deliberately excludes Support).
  "billing.read",
  "billing.write",
  // CONFIG_OVER_CODE: parity with `ManageSettings` in Rust (admin+).
  "settings.write",
];

const SUPER_ADMIN_CAPS: AdminCapability[] = [...ADMIN_CAPS, "users.remove"];

// F2-C: front-desk/cashier — enters the back-office; records payments and
// manages leads, sees metrics (Rust parity: ManagePayments | ManageLeads | ViewMetrics).
const STAFF_CAPS: AdminCapability[] = [
  "admin.access",
  "metrics.read",
  "payments.manage",
  "leads.manage",
];

const CAPABILITIES_BY_ROLE: Record<AdminRole, AdminCapability[]> = {
  super_admin: SUPER_ADMIN_CAPS,
  admin: ADMIN_CAPS,
  support: SUPPORT_CAPS,
  staff: STAFF_CAPS,
  read_only: READ_CAPS,
  user: [],
};

function isAdminRole(role: string | null | undefined): role is AdminRole {
  return typeof role === "string" && (ADMIN_ROLES as readonly string[]).includes(role);
}

/** Narrows an arbitrary string (e.g. `session.user.role`) to a known role. */
export function toAdminRole(role: string | null | undefined): AdminRole {
  return isAdminRole(role) ? role : "user";
}

/** Returns the capability set for a role string (unknown → `user` → none). */
export function capabilitiesForRole(role: string | null | undefined): ReadonlySet<AdminCapability> {
  return new Set(CAPABILITIES_BY_ROLE[toAdminRole(role)]);
}

/** True when the role grants the given capability. */
export function roleCan(role: string | null | undefined, capability: AdminCapability): boolean {
  return capabilitiesForRole(role).has(capability);
}

/** True when the role may enter the admin back-office at all. */
export function isAdminCapable(role: string | null | undefined): boolean {
  return roleCan(role, "admin.access");
}

// ── Ordering between platform roles (mirrors `auth/rbac.rs`) ────────────────
//
// There is deliberately NO numeric `rank`: in Space, `support` and `staff` are
// INCOMPARABLE (each has capabilities the other doesn't), so a linear scale
// would lie. The order is derived from the capability map itself, just like in
// Rust (`outranks`): A dominates B if it has ALL of B's capabilities.
//
// Deriving it from the map — instead of writing a separate table — is what
// keeps the two runtimes in agreement without a third place to update.

/** Capabilities that exist in BOTH runtimes; the rest are UI-only. */
function coreCaps(role: AdminRole): ReadonlySet<AdminCapability> {
  return capabilitiesForRole(role);
}

/** Does `actor` have all of `target`'s capabilities? (non-strict dominance) */
function dominates(actor: AdminRole, target: AdminRole): boolean {
  const a = coreCaps(actor);
  return [...coreCaps(target)].every((c) => a.has(c));
}

/**
 * Can `actor` administer a user whose CURRENT role is `target`?
 * Strictly above, except `super_admin` over its peers (otherwise a dishonest
 * `super_admin` would be untouchable). Mirrors `can_administer`.
 */
export function canAdministerUser(
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
): boolean {
  const actor = toAdminRole(actorRole);
  const target = toAdminRole(targetRole);
  if (actor === "super_admin") {
    return true;
  }
  return dominates(actor, target) && !dominates(target, actor);
}

/**
 * Roles that `actorRole` may GRANT.
 *
 * Replaces the old `ASSIGNABLE_ROLES = ADMIN_ROLES` constant (the entire
 * list, for anyone). A constant's name doesn't force it to say *assignable by
 * whom*; a signature with a parameter does. Mirrors `can_grant_role`: never a
 * role with capabilities the actor doesn't have.
 */
export function assignableRolesFor(actorRole: string | null | undefined): readonly AdminRole[] {
  const actor = toAdminRole(actorRole);
  if (!roleCan(actor, "users.setRole")) {
    return [];
  }
  return ADMIN_ROLES.filter((target) => dominates(actor, target));
}
